import { RSAA } from 'redux-api-middleware'
import { getApiPath } from 'sidecar-utility-library'
import { isNil } from 'ramda'

import { initialState, types } from 'store/Applicant'

/**
 * Validates if an applicant is already registered or not.
 *
 * @param {object} memberInfo {policyId, dob, first/last name}
 * @returns {object} {registered: boolean}
 */
export const checkRegistrationStatus = memberInfo => ({
  [RSAA]: {
    endpoint: getApiPath(`3pr`, `pub/registered`),
    method: 'POST',
    body: JSON.stringify(memberInfo),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [
      types.CHECK_REGISTRATION_STATUS_REQUEST,
      {
        type: types.CHECK_REGISTRATION_STATUS_SUCCESS,
        meta: memberInfo,
      },
      types.CHECK_REGISTRATION_STATUS_ERROR,
    ],
  },
})

/**
 * Verifies if an applicant exists in our system.
 *
 * @param {object} memberInfo (first/last name, dob, ssn, zip)
 * @returns {object} {email: string, success: bool, registered: bool}
 */
export const verifyAccount = memberInfo => ({
  [RSAA]: {
    endpoint: getApiPath(`3pr`, `pub/validate`),
    method: 'POST',
    body: JSON.stringify(memberInfo),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [
      types.VERIFY_ACCOUNT_REQUEST,
      {
        type: types.VERIFY_ACCOUNT_SUCCESS,
        meta: memberInfo,
      },
      types.VERIFY_ACCOUNT_ERROR,
    ],
  },
})

/**
 * POST request to add an ssn to a given member.
 *
 * @param {string} ssn
 * @param {object} memberInfo
 */
export const addSSN = ({ ssn, ...memberInfo }) => ({
  [RSAA]: {
    endpoint: getApiPath(`3pr`, `pub/addSsn`),
    method: 'POST',
    body: JSON.stringify({
      ...memberInfo,
      ssn: ssn,
    }),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [types.ADD_SSN_REQUEST, types.ADD_SSN_SUCCESS, types.ADD_SSN_ERROR],
  },
})

/**
 * POST request to create an account for a given member, with the member's email
 * on file and the password they have submitted.
 *
 * Goes to member-app's `/api/auth/register` (same-origin through the MFE
 * proxy), which holds the 3pr API key server-side and, when the `work-os-auth`
 * LaunchDarkly flag is on, creates the member's WorkOS user before registering
 * with 3pr. The ordering and the `{ message, field? }` error shape are
 * documented on the handler, `apps/member-app/app/api/auth/register/route.ts`.
 *
 * @param {string} password
 * @param {object} memberInfo
 */
export const registerAccount = ({ password, ...memberInfo }) => {
  if (isNil(password)) {
    throw new Error('you have to give a password')
  }

  return {
    [RSAA]: {
      endpoint: '/api/auth/register',
      method: 'POST',
      body: JSON.stringify({
        ...memberInfo,
        password: btoa(password),
      }),
      types: [
        types.REGISTER_ACCOUNT_REQUEST,
        types.REGISTER_ACCOUNT_SUCCESS,
        types.REGISTER_ACCOUNT_ERROR,
      ],
    },
  }
}

export const clearRegistration = () => ({
  type: types.CLEAR_REGISTRATION,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.CHECK_REGISTRATION_STATUS_REQUEST: {
      return {
        ...state,
        checkRegistrationStatus: {
          busy: true,
          error: '',
        },
      }
    }
    case types.CHECK_REGISTRATION_STATUS_SUCCESS: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          primaryApplicant: {
            ...state.applicants.primaryApplicant,
            ...meta,
            registered: payload.registered,
          },
        },
        checkRegistrationStatus: {
          ...state.checkRegistrationStatus,
          busy: false,
        },
      }
    }
    case types.CHECK_REGISTRATION_STATUS_ERROR: {
      return {
        ...state,
        checkRegistrationStatus: {
          ...state.checkRegistrationStatus,
          busy: false,
          error:
            payload?.response?.message || 'Could not check registration status',
        },
      }
    }

    case types.VERIFY_ACCOUNT_REQUEST: {
      return {
        ...state,
        verifyAccount: {
          ...state.verifyAccount,
          busy: true,
          error: '',
        },
      }
    }
    case types.VERIFY_ACCOUNT_SUCCESS: {
      return {
        ...state,
        verifyAccount: {
          ...state.verifyAccount,
          busy: false,
        },
        applicants: {
          ...state.applicants,
          primaryApplicant: {
            ...state.applicants.primaryApplicant,
            ...meta,
            email: payload.email,
            // if already registered, we'll send the user to the dashboard
            registered: payload.registered,
            // is missing an ssn, we'll send the user to the "add ssn" step
            hasSsn: payload.hasSsn,
            // we also get a payload.success, but that's pointless to store
          },
        },
      }
    }
    case types.VERIFY_ACCOUNT_ERROR: {
      return {
        ...state,
        verifyAccount: {
          ...state.verifyAccount,
          busy: false,
          error: payload?.response?.message || 'Could not verify account',
        },
      }
    }

    case types.ADD_SSN_REQUEST: {
      return {
        ...state,
        addSSN: {
          ...state.addSSN,
          busy: true,
          error: '',
        },
      }
    }
    case types.ADD_SSN_SUCCESS: {
      return {
        ...state,
        addSSN: {
          ...state.addSSN,
          busy: false,
          error: '',
        },
        applicants: {
          ...state.applicants,
          primaryApplicant: {
            ...state.applicants.primaryApplicant,
            hasSsn: payload.hasSsn,
          },
        },
      }
    }
    case types.ADD_SSN_ERROR: {
      return {
        ...state,
        addSSN: {
          ...state.addSSN,
          busy: false,
          error: payload?.response?.message || 'Could not verify your identity',
        },
      }
    }

    case types.REGISTER_ACCOUNT_REQUEST: {
      return {
        ...state,
        registerAccount: {
          ...state.registerAccount,
          busy: true,
          error: '',
        },
      }
    }
    case types.REGISTER_ACCOUNT_SUCCESS: {
      return {
        ...state,
        registerAccount: {
          ...state.registerAccount,
          busy: false,
          entity: payload,
        },
      }
    }
    case types.REGISTER_ACCOUNT_ERROR: {
      return {
        ...state,
        registerAccount: {
          ...state.registerAccount,
          busy: false,
          error: payload?.response?.message || 'Could not register account',
          errorField: payload?.response?.field,
        },
      }
    }

    case types.CLEAR_REGISTRATION: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          primaryApplicant: {},
        },
      }
    }

    default:
      return state
  }
}
